import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../lib/api.js';
import { useI18n } from '../i18n/LanguageProvider.js';
import { ErrorNotice } from './ErrorNotice.js';
import { Icon } from './Icons.js';

/**
 * What is actually working, in one place.
 *
 * Every screen in this product was individually truthful and collectively
 * misleading: a connection warning asked for a permission Meta would not issue,
 * five WhatsApp numbers read "receiving messages" on invalidated credentials,
 * and seven Pages sat on a polling delay with nothing saying so. Each fact was
 * correct where it lived. Nobody was told the shape of the whole, so the first
 * anyone knew of it was a message that never arrived.
 *
 * Two rules hold this together:
 *
 *   • **A blocker names who can act.** "Not enabled" is an administrator's
 *     switch, "permission not granted" is a reconnect, "setup" is a step left
 *     undone here. Sending somebody to reconnect for the first of those is how
 *     an afternoon disappears.
 *
 *   • **It never claims to have just checked.** These counts come from stored
 *     state, because this sits on the home screen. Where a belief could be
 *     stale, it says so and points at the screen that re-asks Meta.
 */
/**
 * Which channel each health row belongs to, where the installation may not
 * offer it at all. FACEBOOK has no entry because it is not a channel anybody
 * switches off — it is the product.
 *
 * The filtering happens here rather than in the health service on purpose: the
 * service still measures every channel and still distinguishes a switch nobody
 * flipped from a permission nobody granted, which is what an administrator
 * needs. This screen simply does not draw a row for a channel the dashboard is
 * not offering, the way the tab row does not draw its tab.
 */
const CHANNEL_OF: Record<string, string | undefined> = {
  MESSENGER: 'MESSENGER',
  INSTAGRAM_DM: 'INSTAGRAM',
  WHATSAPP: 'WHATSAPP',
};

export function HealthPanel({ onGo }: { onGo?: (tab: string) => void }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();

  const health = useQuery({ queryKey: ['platform-health'], queryFn: api.platformHealth });
  const me = useQuery({ queryKey: ['me'], queryFn: api.me });
  const offered = new Set(me.data?.channels ?? []);
  const shown = (key: string) => {
    const channel = CHANNEL_OF[key];
    return channel === undefined || offered.has(channel);
  };

  const subscribe = useMutation({
    mutationFn: api.subscribeAllWebhooks,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['platform-health'] });
      void queryClient.invalidateQueries({ queryKey: ['pages'] });
    },
  });

  if (health.isError) return <ErrorNotice error={health.error} />;
  if (!health.data) return null;

  const {
    channels: measured,
    livePages,
    activePages,
    unverifiedNumbers,
    expiringConnections,
  } = health.data;
  const channels = measured.filter((channel) => shown(channel.key));
  const workingChannels = channels.filter((channel) => channel.working > 0).length;
  const behind = activePages - livePages;

  return (
    <section className="panel health">
      <div className="panel-head">
        <h2>{t('health.title')}</h2>
        <span className="muted small">
          {t('health.summary')
            .replace('{working}', String(workingChannels))
            .replace('{total}', String(channels.length))}
        </span>
      </div>

      <ul className="health-list">
        {channels.map((channel) => {
          const ok = channel.working > 0 && channel.blocker === 'NONE';
          return (
            <li key={channel.key} className={ok ? 'health-row ok' : 'health-row'}>
              <span className="health-name">{t(`channel.${channel.key}` as never)}</span>
              <span className="health-count">
                {channel.working} / {channel.possible}
              </span>
              <span className={`health-blocker blocker-${channel.blocker.toLowerCase()}`}>
                {ok ? t('health.working') : t(`health.blocker.${channel.blocker}` as never)}
              </span>
            </li>
          );
        })}
      </ul>

      {/*
        Tokens run out on a schedule nobody watches. A long-lived Meta token
        lasts about sixty days and takes every Page under it down at once when
        it lapses — and `tokenExpiresAt` was written on every connection and
        read by nothing, so the first sign was a customer who never got a reply.

        Placed above the webhook notice deliberately: an expiry stops
        everything, while a Page on polling is merely slower.
      */}
      {expiringConnections.length > 0 && (
        <div className="health-action">
          {expiringConnections.map((connection) => (
            <p
              key={connection.id}
              className={connection.daysLeft < 0 ? 'error small' : 'warn small'}
            >
              {(connection.daysLeft < 0 ? t('health.tokenExpired') : t('health.tokenExpiring'))
                .replace('{account}', connection.label)
                .replace('{days}', String(Math.abs(connection.daysLeft)))
                .replace('{pages}', String(connection.pages))}
            </p>
          ))}
          <button type="button" onClick={() => onGo?.('connections')}>
            <Icon.link />
            {t('health.reconnect')}
          </button>
        </div>
      )}

      {/*
        The difference between a comment arriving in seconds and arriving when
        somebody presses a button. It was invisible before, and it is one
        request to fix for every Page at once.
      */}
      {behind > 0 && (
        <div className="health-action">
          <p className="warn small">{t('health.notLive').replace('{count}', String(behind))}</p>
          <button type="button" onClick={() => subscribe.mutate()} disabled={subscribe.isPending}>
            <Icon.refresh className={subscribe.isPending ? 'spin' : undefined} />
            {subscribe.isPending ? t('health.subscribing') : t('health.subscribeAll')}
          </button>
        </div>
      )}

      {subscribe.isError && <ErrorNotice error={subscribe.error} />}

      {subscribe.isSuccess && (
        <div className="health-result">
          <p className="ok small">
            {t('health.subscribed')
              .replace('{count}', String(subscribe.data.subscribed))
              .replace('{already}', String(subscribe.data.alreadyOn))}
          </p>
          {/* Named individually. A count of failures tells somebody there is a
              problem; the names tell them which Page to look at. */}
          {subscribe.data.failed.map((failure) => (
            <p key={failure.name} className="error small">
              {failure.name}: {failure.reason}
            </p>
          ))}
          {subscribe.data.skipped > 0 && (
            <p className="muted small">
              {t('health.skipped').replace('{count}', String(subscribe.data.skipped))}
            </p>
          )}
        </div>
      )}

      {unverifiedNumbers > 0 && shown('WHATSAPP') && (
        <p className="warn small">
          {t('health.unverified').replace('{count}', String(unverifiedNumbers))}{' '}
          {onGo && (
            <button type="button" className="link" onClick={() => onGo('whatsapp')}>
              {t('whatsapp.title')}
            </button>
          )}
        </p>
      )}
    </section>
  );
}
